import { useState } from 'react';
import { management } from '../lib/api';

/**
 * On/off toggle for an ad group's or an ad's `is_active` — which is exactly the gate the load-ads
 * impression script reads (`ad_groups.is_active:'Y'` / `ads.is_active:'Y'`), so switching it off stops
 * the entity (an ad group, or a single creative) being sent to Redis on the next load. Optimistic and
 * self-contained: it holds its own state and calls the management status endpoint, so no table reload is
 * needed; a failed write reverts and shows why.
 */
export function StatusToggle(
  { id, kind, initialActive }: { id: string; kind: 'adgroup' | 'ad'; initialActive: boolean | null },
) {
  const [on, setOn] = useState<boolean>(initialActive !== false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = async () => {
    const next = !on;
    setBusy(true); setError(null);
    setOn(next); // optimistic
    try {
      if (kind === 'adgroup') await management.adGroups.setStatus(id, next);
      else await management.ads.setStatus(id, next);
    } catch (e) {
      setOn(!next); // revert
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const what = kind === 'adgroup' ? 'this ad group and its ads' : 'this creative';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        disabled={busy}
        onClick={toggle}
        title={`${on ? 'On' : 'Off'} — controls whether ${what} is sent to Redis for the load-ads impression script`}
        style={{
          minWidth: '3.6rem', padding: '0.2rem 0.5rem', borderRadius: '999px', cursor: busy ? 'wait' : 'pointer',
          border: '1px solid ' + (on ? '#2b7a2b' : '#c9c9c9'),
          background: on ? '#2b7a2b' : '#f0f0f0', color: on ? '#fff' : '#666',
          fontSize: '0.78rem', fontWeight: 600, opacity: busy ? 0.6 : 1,
        }}
      >
        {busy ? '…' : on ? 'On' : 'Off'}
      </button>
      {error && <span className="tiny" style={{ color: '#c0392b' }} title={error}>!</span>}
    </span>
  );
}
